import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  FormControlLabel,
  Switch,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ColorLens,
} from '@mui/icons-material';
import {
  HexColorPicker,
  HexColorInput,
} from 'react-colorful';
import DropBoxUploader from './DropBoxUploader';
import { useSelector } from 'react-redux';

const FormAppearanceSettings = ({ open, onClose, editingForm, setEditingForm }) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHeaderColorPicker, setShowHeaderColorPicker] = useState(false);
  const [showFontColorPicker, setShowFontColorPicker] = useState(false);
  const [showHeaderFontColorPicker, setShowHeaderFontColorPicker] = useState(false); // ← ADDED

  const formSettings = editingForm?.form_settings || {};
  const currentColor = formSettings.backgroundColor || '#ffffff';
  const currentHeaderColor = formSettings.sectionHeaderColor || '#f5f5f5';
  const currentFontColor = formSettings.fontColor || '#000000';
  const currentHeaderFontColor = formSettings.sectionHeaderFontColor || '#000000'; // ← ADDED

  const installation = useSelector(state => state.settings_dealerships?.installation || {});
  const dealership = installation.subdomain || 'unknown';

  const updateSetting = (key, value) => {
    setEditingForm(prev => ({
      ...prev,
      form_settings: {
        ...(prev?.form_settings || {}),
        [key]: value,
      },
    }));
  };

  const handleInternalClose = () => {
    setShowColorPicker(false);
    setShowHeaderColorPicker(false);
    setShowFontColorPicker(false);
    setShowHeaderFontColorPicker(false); // ← ADDED
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleInternalClose} maxWidth="sm" fullWidth>
      <DialogTitle>Form Appearance Settings</DialogTitle>
      <DialogContent dividers>
        {/* Icon Key Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={formSettings.showIconKey !== false}
              onChange={e => updateSetting('showIconKey', e.target.checked)}
            />
          }
          label="Show Icon Key at Top of Form"
          sx={{ mb: 3, display: 'block' }}
        />

        {/* Background Color */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Background Color
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ColorLens />}
            onClick={() => setShowColorPicker(!showColorPicker)}
          >
            {currentColor === '#ffffff' ? 'Set Color' : 'Change Color'}
          </Button>

          <Box
            sx={{
              width: 60,
              height: 40,
              bgcolor: currentColor,
              border: '2px solid #ccc',
              borderRadius: 1,
              boxShadow: 2,
            }}
          />

          {currentColor !== '#ffffff' && (
            <Button size="small" onClick={() => updateSetting('backgroundColor', '')}>
              Clear
            </Button>
          )}
        </Stack>

        {showColorPicker && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Stack direction="column" spacing={2} alignItems="center">
              <HexColorPicker
                color={currentColor}
                onChange={(color) => updateSetting('backgroundColor', color)}
              />

              <HexColorInput
                color={currentColor}
                onChange={(color) => updateSetting('backgroundColor', color)}
                prefixed
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '1rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                }}
              />
            </Stack>
          </Paper>
        )}

        {/* Section Header Color */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Section Header Background Color
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ColorLens />}
            onClick={() => setShowHeaderColorPicker(!showHeaderColorPicker)}
          >
            {currentHeaderColor === '#f5f5f5' ? 'Set Color' : 'Change Color'}
          </Button>

          <Box
            sx={{
              width: 60,
              height: 40,
              bgcolor: currentHeaderColor,
              border: '2px solid #ccc',
              borderRadius: 1,
              boxShadow: 2,
            }}
          />

          {currentHeaderColor !== '#f5f5f5' && (
            <Button size="small" onClick={() => updateSetting('sectionHeaderColor', '')}>
              Clear
            </Button>
          )}
        </Stack>

        {showHeaderColorPicker && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Stack direction="column" spacing={2} alignItems="center">
              <HexColorPicker
                color={currentHeaderColor}
                onChange={(color) => updateSetting('sectionHeaderColor', color)}
              />

              <HexColorInput
                color={currentHeaderColor}
                onChange={(color) => updateSetting('sectionHeaderColor', color)}
                prefixed
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '1rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                }}
              />
            </Stack>
          </Paper>
        )}

        {/* Font Color */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Font Color (Form Default)
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ColorLens />}
            onClick={() => setShowFontColorPicker(!showFontColorPicker)}
          >
            {currentFontColor === '#000000' ? 'Set Color' : 'Change Color'}
          </Button>

          <Box
            sx={{
              width: 60,
              height: 40,
              bgcolor: currentFontColor,
              border: '2px solid #ccc',
              borderRadius: 1,
              boxShadow: 2,
            }}
          />

          {currentFontColor !== '#000000' && (
            <Button size="small" onClick={() => updateSetting('fontColor', '')}>
              Clear
            </Button>
          )}
        </Stack>

        {showFontColorPicker && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Stack direction="column" spacing={2} alignItems="center">
              <HexColorPicker
                color={currentFontColor}
                onChange={(color) => updateSetting('fontColor', color)}
              />

              <HexColorInput
                color={currentFontColor}
                onChange={(color) => updateSetting('fontColor', color)}
                prefixed
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '1rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                }}
              />
            </Stack>
          </Paper>
        )}

        {/* Section Header Font Color ← ADDED */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Section Header Font Color (Form Default)
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ColorLens />}
            onClick={() => setShowHeaderFontColorPicker(!showHeaderFontColorPicker)}
          >
            {currentHeaderFontColor === '#000000' ? 'Set Color' : 'Change Color'}
          </Button>

          <Box
            sx={{
              width: 60,
              height: 40,
              bgcolor: currentHeaderFontColor,
              border: '2px solid #ccc',
              borderRadius: 1,
              boxShadow: 2,
            }}
          />

          {currentHeaderFontColor !== '#000000' && (
            <Button size="small" onClick={() => updateSetting('sectionHeaderFontColor', '')}>
              Clear
            </Button>
          )}
        </Stack>

        {showHeaderFontColorPicker && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Stack direction="column" spacing={2} alignItems="center">
              <HexColorPicker
                color={currentHeaderFontColor}
                onChange={(color) => updateSetting('sectionHeaderFontColor', color)}
              />

              <HexColorInput
                color={currentHeaderFontColor}
                onChange={(color) => updateSetting('sectionHeaderFontColor', color)}
                prefixed
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '1rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                }}
              />
            </Stack>
          </Paper>
        )}

        {/* Background Image */}
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Background Image
        </Typography>

        <DropBoxUploader
          installation={dealership}
          siteid={`${dealership}_form_bg`}
          value={formSettings.backgroundImage || ''}
          onChange={(url) => updateSetting('backgroundImage', url)}
        />

        {formSettings.backgroundImage && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <img
              src={formSettings.backgroundImage}
              alt="Form background preview"
              style={{
                maxWidth: '100%',
                maxHeight: 200,
                borderRadius: 8,
                objectFit: 'cover',
                border: '1px solid #ddd',
              }}
            />
            <br />
            <Button
              size="small"
              color="error"
              sx={{ mt: 1 }}
              onClick={() => updateSetting('backgroundImage', '')}
            >
              Remove Image
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleInternalClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormAppearanceSettings;